import { arteDa, type Folha, type Quadro } from '../world/spriteAtlas';
import { corDoMaterial, deveTingir as deveTingirMaterial, rampaDoMaterial, type Divisor, type Material } from './materiais';

export const PELE_MIN = 1002;
export const PELE_MAX = 1058;
export const PELE_PADRAO = 1009;

export function peleValida(hue?: number): hue is number {
  return hue != null && hue >= PELE_MIN && hue <= PELE_MAX;
}

export function peleNoIntervalo(hue?: number): number {
  return peleValida(hue) ? hue : PELE_PADRAO;
}

/** Mesma rampa do paperdoll da criação: 1002 claro → 1058 escuro. */
export function peleHsl(hue: number): { h: number; s: number; l: number } {
  const t = Math.min(1, Math.max(0, (peleNoIntervalo(hue) - PELE_MIN) / (PELE_MAX - PELE_MIN)));
  return { h: 28, s: 36 + t * 8, l: 72 - t * 42 };
}

export function peleCss(hue: number): string {
  const { h, s, l } = peleHsl(hue);
  return `hsl(${h} ${s}% ${l}%)`;
}

export function peleRgb(hue: number): { r: number; g: number; b: number } {
  const { h, s, l } = peleHsl(hue);
  return hslParaRgb(h, s / 100, l / 100);
}

function hslParaRgb(h: number, s: number, l: number): { r: number; g: number; b: number } {
  const hh = (((h % 360) + 360) % 360) / 360;
  const hue2rgb = (p: number, q: number, t: number) => {
    let x = t;
    if (x < 0) x += 1;
    if (x > 1) x -= 1;
    if (x < 1 / 6) return p + (q - p) * 6 * x;
    if (x < 1 / 2) return q;
    if (x < 2 / 3) return p + (q - p) * (2 / 3 - x) * 6;
    return p;
  };
  if (s === 0) {
    const v = Math.round(l * 255);
    return { r: v, g: v, b: v };
  }
  const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
  const p = 2 * l - q;
  return {
    r: Math.round(hue2rgb(p, q, hh + 1 / 3) * 255),
    g: Math.round(hue2rgb(p, q, hh) * 255),
    b: Math.round(hue2rgb(p, q, hh - 1 / 3) * 255)
  };
}

/**
 * Cache das folhas tingidas, com teto. Cada entrada é uma folha inteira em RGBA
 * — ~2 MB — e a chave inclui o tom de pele, então uma cidade com muitos
 * jogadores de tons diferentes enchia isso sem parar: era um vazamento lento,
 * só invisível porque a célula antiga era pequena. O teto é por folha×tom, e a
 * ordem de inserção do Map faz o despejo do menos usado. Despejar não quebra
 * nada: quem já está desenhando segue com a referência, e o próximo pedido
 * tinge de novo.
 */
const TINGIDAS_MAX = 24;
const tingidas = new Map<string, Folha>();

function deveTingir(folha: Folha): boolean {
  return (folha.url.includes('/human-male/') || folha.url.includes('/human-female/'))
    && !folha.url.includes('/cloth/')
    && !folha.url.includes('/hair/')
    && !folha.url.includes('/beard/');
}

/** Placas e armas/escudo na mão (`/character/weapons/`) levam a cor do material. */
function folhaLevaMaterial(folha: Folha): boolean {
  // Couro cravejado e osso entram com os materiais deles (proposta-armaduras-couro-e-osso).
  return (folha.url.includes('/plates/') || folha.url.includes('/weapons/')
    || folha.url.includes('/studded/') || folha.url.includes('/bone/')) && !folha.url.includes('#mat');
}

/** Placas mantêm o 160 de sempre; arma e escudo usam o brilho médio da folha. */
function divisorDaFolha(folha: Folha): Divisor {
  return folha.url.includes('/plates/') ? 160 : 'media';
}

/** Brilho médio dos pixels visíveis: o divisor que faz a média da arte cair na cor. */
function brilhoMedio(d: Uint8ClampedArray, preservar?: (r: number, g: number, b: number) => boolean): number {
  let soma = 0;
  let n = 0;
  for (let i = 0; i < d.length; i += 4) {
    if (d[i + 3] === 0) continue;
    if (preservar?.(d[i], d[i + 1], d[i + 2])) continue;
    soma += d[i] * 0.299 + d[i + 1] * 0.587 + d[i + 2] * 0.114;
    n++;
  }
  return n > 0 && soma > 0 ? soma / n : 160;
}

/**
 * Tinge a FOLHA INTEIRA, uma vez, e devolve um canvas só. Já foi quadro por
 * quadro: com 8 direções pré-recortadas isso custava 27 MB por tom de pele,
 * num cache que nunca esvazia. A folha inteira é 5/8 disso e não duplica as
 * direções espelhadas, que agora saem de uma transformada na hora de desenhar.
 */
/**
 * Matiz cheia da UO: cada pixel visível vira o tom `vermelho >> 3` (0..31) da rampa do material, como o cliente faz
 * com o item de metal (techspec-materiais, Renato 30/10). Transparente fica transparente.
 */
export function tingirFonteRampa(src: CanvasImageSource, w: number, h: number, rampa: Uint8ClampedArray): CanvasImageSource {
  const c = document.createElement('canvas');
  c.width = w;
  c.height = h;
  const g = c.getContext('2d', { willReadFrequently: true });
  if (!g) return src;
  g.imageSmoothingEnabled = false;
  g.drawImage(src, 0, 0);
  const img = g.getImageData(0, 0, w, h);
  const d = img.data;
  for (let i = 0; i < d.length; i += 4) {
    if (d[i + 3] === 0) continue;
    const k = (d[i] >> 3) * 3;
    d[i] = rampa[k];
    d[i + 1] = rampa[k + 1];
    d[i + 2] = rampa[k + 2];
  }
  g.putImageData(img, 0, 0);
  return c;
}

export function tingirFonte(
  src: CanvasImageSource,
  w: number,
  h: number,
  rgb: { r: number; g: number; b: number },
  divisor: Divisor = 160,
  preservar?: (r: number, g: number, b: number) => boolean
): CanvasImageSource {
  const c = document.createElement('canvas');
  c.width = w;
  c.height = h;
  const g = c.getContext('2d', { willReadFrequently: true });
  if (!g) return src;
  g.imageSmoothingEnabled = false;
  g.drawImage(src, 0, 0);
  const img = g.getImageData(0, 0, w, h);
  const d = img.data;
  const den = divisor === 'media' ? brilhoMedio(d, preservar) : (divisor > 0 ? divisor : 160);
  for (let i = 0; i < d.length; i += 4) {
    if (d[i + 3] === 0) continue;
    if (preservar?.(d[i], d[i + 1], d[i + 2])) continue;
    const y = (d[i] * 0.299 + d[i + 1] * 0.587 + d[i + 2] * 0.114) / den;
    d[i] = Math.min(255, Math.round(rgb.r * y));
    d[i + 1] = Math.min(255, Math.round(rgb.g * y));
    d[i + 2] = Math.min(255, Math.round(rgb.b * y));
  }
  g.putImageData(img, 0, 0);
  return c;
}

/** Largura/altura da folha em pixels — os recortes cobrem a grade inteira. */
function tamanhoDaFolha(folha: Folha): { w: number; h: number } {
  let w = 0;
  let h = 0;
  for (const linha of folha.dirs) {
    for (const q of linha) {
      w = Math.max(w, q.sx + q.w);
      h = Math.max(h, q.sy + q.h);
    }
  }
  return { w, h };
}

/** Multiplica o cinza do human-male pela cor da pele. Ghost e outras folhas ficam iguais. */
export function tingirFolha(folha: Folha, hue: number): Folha {
  if (!deveTingir(folha)) return folha;
  const pele = peleNoIntervalo(hue);
  const key = `${folha.url}#${pele}#${folha.frames}`;
  const hit = tingidas.get(key);
  if (hit) {
    tingidas.delete(key);                       // reinsere: vira o mais recente
    tingidas.set(key, hit);
    return hit.fps === folha.fps ? hit : { ...hit, fps: folha.fps };
  }
  const { w, h } = tamanhoDaFolha(folha);
  const fonte = tingirFonte(folha.fonte, w, h, peleRgb(pele));
  const next: Folha = {
    ...folha,
    url: key,
    fonte,
    dirs: folha.dirs.map(row => row.map(q => ({ ...q, fonte })))
  };
  tingidas.set(key, next);
  while (tingidas.size > TINGIDAS_MAX) {
    const velha = tingidas.keys().next();
    if (velha.done) break;
    tingidas.delete(velha.value);
  }
  return next;
}

const TINGIDAS_MATERIAL_MAX = 64;
const tingidasMaterial = new Map<string, Folha>();

/**
 * Multiplica o cinza das placas, da arma e do escudo pela cor do material
 * (techspec-materiais). O padrão (Iron, madeira comum) não tinge.
 */
export function tingirFolhaMaterial(folha: Folha, material: Material | null | undefined): Folha {
  if (!deveTingirMaterial(material) || !folhaLevaMaterial(folha)) return folha;
  const rgb = corDoMaterial(material);
  if (!rgb) return folha;
  const rampa = rampaDoMaterial(material);
  const key = `${folha.url}#mat${material.id}${rampa ? 'uo' : ''}#${folha.frames}`;
  const hit = tingidasMaterial.get(key);
  if (hit) {
    tingidasMaterial.delete(key);
    tingidasMaterial.set(key, hit);
    return hit.fps === folha.fps ? hit : { ...hit, fps: folha.fps };
  }
  const { w, h } = tamanhoDaFolha(folha);
  const fonte = rampa ? tingirFonteRampa(folha.fonte, w, h, rampa) : tingirFonte(folha.fonte, w, h, rgb, divisorDaFolha(folha));
  const next: Folha = {
    ...folha,
    url: key,
    fonte,
    dirs: folha.dirs.map(row => row.map(q => ({ ...q, fonte })))
  };
  tingidasMaterial.set(key, next);
  while (tingidasMaterial.size > TINGIDAS_MATERIAL_MAX) {
    const velha = tingidasMaterial.keys().next();
    if (velha.done) break;
    tingidasMaterial.delete(velha.value);
  }
  return next;
}

export function artePele(folha: Folha, hue: number, dir: number, quadro: number): Quadro | undefined {
  return arteDa(tingirFolha(folha, hue), dir, quadro);
}
